import * as React from "react";
import { forwardRef, Ref, useCallback, useState } from "react";
import styled from "@mui/material/styles/styled";
import {
  SearchComponent,
  UserEnterSymptomsMode,
} from "../../search/searchComponent";
import { useTranslate } from "../../../hooks/useTranslate";
import { FeatureWithName } from "fe-web-components/autocomplete";
import {
  ALLERGIES_FEATURE_ID,
  FREE_TEXT_FEATURE_ID,
  KahunCategories,
  Relations,
} from "shared";

import { QualifiedFeature } from "fe-common/graphql";
import {
  convertFreeTextQF,
  getFreeTextFromQF,
} from "fe-common/logic/qualifiedFeature/freeText";
import { featureToLines } from "fe-common/logic";
import { commonBoxShadowStyle } from "fe-common/ui/boxShadow";
import { NumericQuestionLayout } from "../rangeQuestions/numericQuestions/numericQuestionLayout";
//region [[ Styles ]]
const AllergySelectionView = styled("div")(({ theme }) => ({}));
//endregion [[ Styles ]]
//region [[ Props ]]
export interface AllergySelectionProps {
  onAllergyChange: (qf: QualifiedFeature) => void;
  ref: Ref<HTMLDivElement>;
  isSecond?: boolean;
}
//endregion [[ Props ]]
//region [[ Functions ]]
//endregion [[ Functions ]]

const panelSearchOptionsAllergies = {
  panelIds: null,
  patientFacing: true,
  categories: [KahunCategories.ALLERGIES],
};

const panelSearchOptionsReactions = {
  panelIds: null,
  patientFacing: true,
  categories: [KahunCategories.SIGNS_SYMPTOMS, KahunCategories.KAHUN_SYMPTOMS],
};

const AllergySearchComponent = styled(SearchComponent)({
  width: "100%",
  marginTop: "10px",
  marginBottom: "10px",
  "& .list-container": {
    background: "white",
  },
  "& .search-view": {
    background: "white",
    ...commonBoxShadowStyle,
  },
  "& .ak-feature-selection-input": {
    padding: "5px 23px",
  },
});

const ReactionQuestionLayout = styled(NumericQuestionLayout)({
  marginBottom: "15px",
  "& .question-container": {
    marginBottom: 0,
  },
});

export const AllergySelectionForm = forwardRef(
  ({
    onAllergyChange,
    ref,
    isSecond = false,
    ...props
  }: AllergySelectionProps) => {
    const { translate } = useTranslate();

    const [allergy, setAllergy] = useState<QualifiedFeature>(null);

    const handleAllergySelected = useCallback(
      (event: React.ChangeEvent<{}>, value: FeatureWithName | null) => {
        let selectedQf = value.value;
        selectedQf =
          selectedQf.feature.id === FREE_TEXT_FEATURE_ID
            ? (convertFreeTextQF(selectedQf, {
                featureId: ALLERGIES_FEATURE_ID,
                relationId: Relations.EXPOSURE_TO,
              }) as QualifiedFeature)
            : selectedQf;
        setAllergy(selectedQf);
      },
      [],
    );

    const reset = () => {
      setAllergy(null);
    };

    const allergyReactionQuestionsText =
      translate("ALLERGIES_REACTION_QUESTION") +
      (allergy
        ? " " +
          featureToLines(allergy, false, [Relations.EXPOSURE_TO])[0]
            .toLowerCase()
            .trim()
        : "") +
      "?";

    const handleReactionSelected = useCallback(
      (event: React.ChangeEvent<{}>, value: FeatureWithName | null) => {
        const qualifiers = [...allergy.qualifiers];

        if (value) {
          const selectedQf = value?.value;
          const reaction = {
            relation: {
              id: Relations.ALLERGY_REACTIONS,
            },
            value: selectedQf.feature,
            freeText: getFreeTextFromQF(selectedQf),
          } as any;
          qualifiers.push(reaction);
        }

        onAllergyChange?.({
          ...allergy,
          qualifiers,
        });

        reset();
      },
      [allergy, onAllergyChange],
    );

    return (
      <AllergySelectionView ref={ref}>
        {!allergy && (
          <AllergySearchComponent
            onChange={handleAllergySelected}
            placeholder={translate(
              isSecond
                ? "AUTO_COMPLETE_ALLERGY_SECOND"
                : "AUTO_COMPLETE_ALLERGY",
            )}
            searchOptions={panelSearchOptionsAllergies}
            noIcon
            autoFocus
            userEnterSymptom={UserEnterSymptomsMode.FREE_TEXT}
            enterSymptomTextOverride={{
              titleText: "ENTER_ALLERGY_DESCRIPTION",
              cantFindText: "CANT_FIND_ALLERGY",
              placeholderText: "ENTER_YOUR_ALLERGY",
              thankYouText: "THANKS_ENTER_SYMPTOM",
            }}
          />
        )}
        {allergy && (
          <ReactionQuestionLayout
            questionText={allergyReactionQuestionsText}
            isAnswered={false}
            textAsQuestion={false}
            onSkipClicked={() => handleReactionSelected(null, null)}
            preventSubmit
          >
            <AllergySearchComponent
              onChange={handleReactionSelected}
              placeholder={translate("AUTO_COMPLETE_REACTION")}
              searchOptions={panelSearchOptionsReactions}
              autoFocus
              noIcon
              userEnterSymptom={UserEnterSymptomsMode.FREE_TEXT}
              enterSymptomTextOverride={{
                titleText: "ENTER_ALLERGY_REACTION_DESCRIPTION",
                cantFindText: "CANT_FIND_REACTION",
                placeholderText: "ENTER_YOUR_REACTION",
                thankYouText: "THANKS_ENTER_SYMPTOM",
              }}
            />
          </ReactionQuestionLayout>
        )}
      </AllergySelectionView>
    );
  },
);
